<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useTableStore } from '@/stores/tableStore.ts'
import Backend from '@/models/backend.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores / models
 */
const tableStore = useTableStore()
const shipActionStore = useShipActionStore()
const gameStore = useGameStore()
const mapStore = useMapStore()

/**
 * Variables
 */
const isOpen = ref(false)
const menuRef = ref(null)

/**
 * Functions
 */
function toggleMenu() {
  isOpen.value = !isOpen.value
}

// Perform actions on the map with selected shipsshipActionData.data
const actionMap = () => {
  if (tableStore.mapFleetShips.selectedRowIds.length > 0) {
    shipActionStore.initShipsFromIds(tableStore.mapFleetShips.selectedRowIds)
    tableStore.mapFleetShips.selectedRowIds = []
    tableStore.mapFleetShips.selectedAll = false
    gameStore.modalType = ''
  }
}

/**
 * Attach ships to a fleet
 */
const attachToFleet = () => {
  if (tableStore.mapFleetShips.selectedRowIds.length > 0) {
    gameStore.modalType = 'assignToFleet'
    gameStore.modalData = tableStore.mapFleetShips.selectedRowIds
    tableStore.mapFleetShips.selectedRowIds = []
    tableStore.mapFleetShips.selectedAll = false
  }
}

/**
 * Detach ships from a fleet
 */
const detachFromFleet = () => {
  if (tableStore.mapFleetShips.selectedRowIds.length > 0) {
    const backend = new Backend()
    backend.detachShipsFromFleet(tableStore.mapFleetShips.selectedRowIds)
    tableStore.mapFleetShips.selectedRowIds = []
    tableStore.mapFleetShips.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

function closeMenu() {
  isOpen.value = false
}

function handleClickOutside(event) {
  if (!menuRef.value.contains(event.target)) {
    closeMenu()
  }
}

const destroyFleet = () => {
  if (tableStore.mapFleetShips.selectedRowIds.length > 0) {
    gameStore.modal2Type = 'confirmationDelete'
    gameStore.modal2Data = {
      title: t('common.menu.ships.destroyConfirmTitle'),
      message: t('common.menu.ships.destroyTheseConfirmMessage'),
      actionText: t('common.actions.yes'),
      cancelText: t('common.actions.no'),
      action: () => {
        const backend = new Backend()
        backend.destroyShips(tableStore.mapFleetShips.selectedRowIds)
        tableStore.mapFleetShips.selectedRowIds = []
        tableStore.mapFleetShips.selectedAll = false
      }
    }
  }
}

/**
 * Lifecycle hooks
 */
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="relative" @click.stop="toggleMenu">
    <!-- Icon Button -->
    <ActionButton class="my-1">{{ t('common.actions.action') }}</ActionButton>
    <!-- Dropdown Menu -->
    <Transition name="menu">
      <div
        v-show="isOpen"
        ref="menuRef"
        class="absolute w-52 rounded-lg border border-metal-800 bg-metal-950 z-50 mt-1 overflow-hidden"
      >
        <div class="menu-item" @click="actionMap">
          <GenericIcon type="cmd_decouple" class="mr-3" />
          <span>{{ t('common.menu.ships.detachAndCommand') }}</span>
        </div>
        <div class="menu-item text-red-400" @click="destroyFleet">
          <GenericIcon type="trash" class="mr-3" />
          <span>{{ t('common.menu.ships.destroyShips') }}</span>
        </div>
        <div class="divider"></div>
        <div class="menu-item" @click="attachToFleet">
          <GenericIcon type="add" class="mr-3" />
          <span>{{ t('common.menu.ships.attachToAnotherFleet') }}</span>
        </div>
        <div class="menu-item" @click="detachFromFleet">
          <GenericIcon type="remove" class="mr-3" />
          <span>{{ t('common.menu.ships.detachFromFleet') }}</span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.menu-item {
  @apply flex items-center p-2 text-metal-300 text-sm cursor-pointer;
  transition: background-color 150ms ease;

  &:hover {
    @apply bg-metal-800;
  }

  &:active {
    @apply bg-metal-700;
  }
}

.divider {
  @apply h-px mx-2 my-1 bg-metal-800;
}

/* Menu transition */
.menu-enter-active,
.menu-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
